'use client';

import { Ga4PageViewTracker } from '@v2/analytics/Ga4PageViewTracker';
import { initializeGa4, shouldTrackGa4Pathname } from '@v2/analytics/ga4';
import Script from 'next/script';
import { usePathname } from 'next/navigation';
import { Suspense, useEffect, useRef, useState } from 'react';
import { createSingleRunInitializer } from './single-run-initializer';

const TRUSTED_INTERACTION_EVENTS = [
  'keydown',
  'pointerdown',
  'touchstart',
] as const;

/** Fallback so bounce / non-interactive views still get pageview tags. */
const ANALYTICS_FALLBACK_MS = 4000;

const CHAT_DISABLED_PATH_PREFIXES = [
  '/build-package',
  '/buy',
  '/checkout',
  '/confirmation',
  '/dashboard',
  '/search-flights',
  '/search-hotel',
  '/search-tickets',
];

const GTM_CONTAINER_ID = process.env.NEXT_PUBLIC_GTM_CONTAINER_ID;

function useAnalyticsAfterInteraction(isBookingFlow: boolean) {
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;

    const removeListeners = () => {
      TRUSTED_INTERACTION_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, enableAnalytics);
      });
    };

    const initialize = createSingleRunInitializer(() => {
      setIsEnabled(true);
      removeListeners();
      if (fallbackTimer !== undefined) {
        clearTimeout(fallbackTimer);
        fallbackTimer = undefined;
      }
    });

    const enableAnalytics = (event: Event) => {
      if (!event.isTrusted || isBookingFlow) return;
      initialize();
    };

    TRUSTED_INTERACTION_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, enableAnalytics, {
        passive: true,
      });
    });

    fallbackTimer = setTimeout(
      () => {
        initialize();
      },
      isBookingFlow ? 10000 : ANALYTICS_FALLBACK_MS
    );

    return () => {
      removeListeners();
      if (fallbackTimer !== undefined) {
        clearTimeout(fallbackTimer);
      }
    };
  }, [isBookingFlow]);

  return isEnabled;
}

function initializeAmplitude() {
  const amplitude = (window as any).amplitude;
  if (!amplitude?.init) return;

  const sessionReplayPlugin = amplitude.sessionReplay?.plugin?.({
    sampleRate: 1,
  });

  if (sessionReplayPlugin) {
    amplitude.add(sessionReplayPlugin);
  }

  amplitude.init(process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY, {
    autocapture: true,
    fetchRemoteConfig: true,
  });
}

export function ThirdPartyIntegrations() {
  const pathname = usePathname();
  const isBookingFlow =
    /^\/(build-package|buy|checkout|search-flights|search-hotel|search-tickets)(\/|$)/.test(
      pathname ?? ''
    );
  const analyticsEnabled = useAnalyticsAfterInteraction(isBookingFlow);
  const gaMeasurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  const shouldLoadGoogleAnalytics = shouldTrackGa4Pathname(pathname ?? '');
  const [zendeskLoaded, setZendeskLoaded] = useState(false);
  const shouldShowChat = !CHAT_DISABLED_PATH_PREFIXES.some((pathPrefix) =>
    (pathname ?? '').startsWith(pathPrefix)
  );

  const lastTrackedGtmPathname = useRef<string>();

  // Track SPA route changes in dataLayer for Google Tag Manager (GTM)
  useEffect(() => {
    if (!GTM_CONTAINER_ID || !pathname) return;
    if (lastTrackedGtmPathname.current === pathname) return;
    lastTrackedGtmPathname.current = pathname;

    const dataLayer = ((window as any).dataLayer =
      (window as any).dataLayer || []);
    dataLayer.push({
      event: 'page_view',
      page_path: pathname,
      page_location: typeof window !== 'undefined' ? window.location.href : '',
      page_title: typeof document !== 'undefined' ? document.title : '',
    });
  }, [pathname]);

  // Keep the native Zendesk launcher visible on public pages; hide on booking/dashboard flows.
  useEffect(() => {
    if (!zendeskLoaded) return;

    (window as any).zE?.('messenger', shouldShowChat ? 'show' : 'hide');
  }, [shouldShowChat, zendeskLoaded]);

  useEffect(() => {
    if (gaMeasurementId && shouldLoadGoogleAnalytics) initializeGa4();
  }, [gaMeasurementId, shouldLoadGoogleAnalytics]);

  return (
    <>
      {GTM_CONTAINER_ID && (!isBookingFlow || analyticsEnabled) && (
        <>
          <Script id="google-tag-manager" strategy="afterInteractive">
            {`
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');
            `}
          </Script>
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
        </>
      )}

      {gaMeasurementId && shouldLoadGoogleAnalytics && (
        <>
          <Script
            id="ga4-library"
            src={`https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`}
            strategy="afterInteractive"
          />

          <Script id="google-ads-configuration" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              window.gtag = window.gtag || function gtag(){window.dataLayer.push(arguments);};
              if (!window.__googleAdsConfigured) {
                window.gtag('config', 'AW-1046394993');
                window.__googleAdsConfigured = true;
              }
            `}
          </Script>

          <Suspense fallback={null}>
            <Ga4PageViewTracker />
          </Suspense>
        </>
      )}

      {analyticsEnabled && (
        <>
          <Script id="facebook-pixel" strategy="afterInteractive">
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window,document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '674296612189225');
              fbq('track', 'PageView');
            `}
          </Script>

          {process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID && (
            <Script id="clarity-script" strategy="afterInteractive">
              {`
                (function(c,l,a,r,i,t,y){
                  c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                  t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                  y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                })(window, document, "clarity", "script", "${process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID}");
                var anonymousUserId = document.cookie.match(/(?:^|; )anonymous-user-id=([^;]*)/);
                if (anonymousUserId) window.clarity('set', 'anonymousUserId', decodeURIComponent(anonymousUserId[1]));
              `}
            </Script>
          )}

          {process.env.NEXT_PUBLIC_RB2B_KEY && (
            <Script id="rb2b-script" strategy="afterInteractive">
              {`!function () {
                var reb2b = (window.reb2b = window.reb2b || []);
                if (reb2b.invoked) return;
                reb2b.invoked = true;
                reb2b.methods = ["identify", "collect"];
                reb2b.factory = function (method) {
                  return function () {
                    var args = Array.prototype.slice.call(arguments);
                    args.unshift(method);
                    reb2b.push(args);
                    return reb2b;
                  };
                };
                for (var i = 0; i < reb2b.methods.length; i++) {
                  var key = reb2b.methods[i];
                  reb2b[key] = reb2b.factory(key);
                }
                reb2b.load = function (key) {
                  var script = document.createElement("script");
                  script.type = "text/javascript";
                  script.async = true;
                  script.src = "https://s3-us-west-2.amazonaws.com/b2bjsstore/b/" + key + "/reb2b.js.gz";
                  var first = document.getElementsByTagName("script")[0];
                  first.parentNode.insertBefore(script, first);
                };
                reb2b.SNIPPET_VERSION = "1.0.1";
                reb2b.load("${process.env.NEXT_PUBLIC_RB2B_KEY}");
              }();`}
            </Script>
          )}

          {process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY && (
            <Script
              id="amplitude-script"
              src={`https://cdn.amplitude.com/script/${process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY}.js`}
              strategy="afterInteractive"
              onLoad={initializeAmplitude}
            />
          )}
        </>
      )}

      {/* Auto-load Zendesk messenger widget (not click-to-load). Hide on booking/dashboard paths. */}
      <Script
        id="ze-snippet"
        src="https://static.zdassets.com/ekr/snippet.js?key=b46bd4d2-d677-40e7-8a61-7f25b8b54678"
        strategy="afterInteractive"
        onLoad={() => {
          setZendeskLoaded(true);
          if (!shouldShowChat) {
            (window as any).zE?.('messenger', 'hide');
          }
        }}
      />
    </>
  );
}
